import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import { KeyTokensCookieStorage } from '@modules/auth-module/constants/key-tokens-cookie-storage'
import { LogoutInterface } from '@modules/auth-module/interfaces/logout.interface'
import { SignInResponseInterface } from '@modules/auth-module/interfaces/sign-in-response.interface'
import { SignInRequestInterface } from '@modules/auth-module/interfaces/sign-in-request.interface'
import { TokensInterface } from '@modules/auth-module/interfaces/tokens.interface'
import { RefreshTokenResponseInterface } from '@modules/user-module/interfaces/refresh-token-response.interface'
import { Observable, of, tap } from 'rxjs'
import { EnvironmentsHelperService } from '@environments/core/services/environments-helper.service'
import { UserHelperService } from '@modules/user-module/services/user-helper.service'
import { OneMonthInMs } from '@shared/constants/times/one-month-in-ms'
import { SignUpRequestInterface } from '@modules/auth-module/interfaces/sign-up-request.interface'
import { SignUpValidateFieldRequestInterface } from '@modules/auth-module/interfaces/sign-up-validate-field-request.interface'
import { ConfirmationCodeRequestInterface } from '@modules/auth-module/interfaces/confirmation-code-request.interface'
import { ConfirmationCodeResponseInterface } from '@modules/auth-module/interfaces/confirmation-code-response.interface'
import { NewPasswordResponseInterface } from '@modules/auth-module/interfaces/new-password-response.interface'
import { NewPasswordRequestInterface } from '@modules/auth-module/interfaces/new-password-request.interface'
import { DropPasswordResponseInterface } from '@modules/auth-module/interfaces/drop-password-response.interface'
import { DropPasswordRequestInterface } from '@modules/auth-module/interfaces/drop-password-request.interface'
import { SignUpValidateFieldResponseType } from '@modules/auth-module/types/sign-up-validate-field-response.type'

@Injectable({
	providedIn: 'root',
})
export class AuthService {
	protected readonly json: JSON = JSON
	protected readonly date: DateConstructor = Date
	protected readonly keyTokensCookieStorage: string = KeyTokensCookieStorage
	protected readonly oneMonthInMs: number = OneMonthInMs

	private readonly _http: HttpClient = inject<HttpClient>(HttpClient)
	private readonly _environmentsHelperService: EnvironmentsHelperService =
		inject<EnvironmentsHelperService>(EnvironmentsHelperService)
	private readonly _userHelperService: UserHelperService = inject<UserHelperService>(UserHelperService)

	get tokens(): TokensInterface | null {
		const cookies: string[] = document.cookie.split('; ')
		const found: string | undefined = cookies.find((row: string): boolean =>
			row.startsWith(`${this.keyTokensCookieStorage}=`),
		)

		if (!found) return null
		const value: string | null = found.split('=')[1] ?? null
		return this.json.parse(decodeURIComponent(value ?? 'null'))
	}

	get isAuthorized(): boolean {
		return !!this.tokens
	}

	private _saveTokens(tokens: TokensInterface): void {
		cookieStore.set({
			name: this.keyTokensCookieStorage,
			value: this.json.stringify(tokens),
			expires: this.date.now() + this.oneMonthInMs,
			sameSite: 'strict',
		})
	}

	public deleteTokens(): void {
		cookieStore.delete(this.keyTokensCookieStorage)
	}

	public signIn(data: SignInRequestInterface): Observable<SignInResponseInterface> {
		if (!this._userHelperService.deviceId) {
			this._userHelperService.generateDeviceId()
		}
		if (!this._userHelperService.deviceId) return of()

		return this._http
			.post<SignInResponseInterface>(`${this._environmentsHelperService.apiUrl}/auth_db/login/`, data, {
				headers: new HttpHeaders({
					'device-id': this._userHelperService.deviceId,
				}),
			})
			.pipe(tap((response: SignInResponseInterface): void => this._saveTokens(response.tokens)))
	}

	public registration(data: SignUpRequestInterface): Observable<SignInResponseInterface> {
		return this._http.post<SignInResponseInterface>(
			`${this._environmentsHelperService.apiUrl}/auth_db/registration/`,
			data,
		)
	}

	public validateRegistrationField(
		data: SignUpValidateFieldRequestInterface,
	): Observable<SignUpValidateFieldResponseType> {
		const params: HttpParams = new HttpParams().append('type', data.type).append('value', data.value)
		return this._http.get<SignUpValidateFieldResponseType>(
			`${this._environmentsHelperService.apiUrl}/auth_db/validate_registration/`,
			{ params },
		)
	}

	public requestDropPassword(data: DropPasswordRequestInterface): Observable<DropPasswordResponseInterface> {
		return this._http.post<DropPasswordResponseInterface>(
			`${this._environmentsHelperService.apiUrl}/auth/request_reset_password_v2/`,
			data,
		)
	}

	public sendConfirmationCode(data: ConfirmationCodeRequestInterface): Observable<ConfirmationCodeResponseInterface> {
		return this._http.post<ConfirmationCodeResponseInterface>(
			`${this._environmentsHelperService.apiUrl}/auth/password_reset_complete_v2/`,
			data,
		)
	}

	public resetPassword(data: NewPasswordRequestInterface): Observable<NewPasswordResponseInterface> {
		return this._http.post<NewPasswordResponseInterface>(
			`${this._environmentsHelperService.apiUrl}/auth/password_reset_complete_v2/`,
			data,
		)
	}

	public logout(): Observable<number> {
		const body: LogoutInterface = {
			refresh: this.tokens?.refresh ?? '',
		}
		return this._http.post<number>(`${this._environmentsHelperService.apiUrl}/auth_db/logout/`, body)
	}

	public updateAccessByRefresh(): Observable<RefreshTokenResponseInterface> {
		const refresh: string = this.tokens?.refresh || ''

		return this._http
			.post<RefreshTokenResponseInterface>(`${this._environmentsHelperService.apiUrl}/auth/token/refresh/`, {
				refresh,
			})
			.pipe(
				tap((response: RefreshTokenResponseInterface) => {
					this._saveTokens({
						access: response.access,
						refresh,
					})
				}),
			)
	}
}
